import Billing from '@web-solutions/module-billing';
import { getPublicKeys } from '@web-solutions/react-billing';

import { applyFoodzeeAuthTokens } from './applyFoodzeeAuthTokens';

type Params = {
  email: string;
  dispatch: any;
  paymentSystem?: string;
  // Pre-created user's auth payload (e.g. from /profile/{id}/email in the
  // subscription flow). When provided, we authorize with it instead of
  // creating a Stripe customer (POST /stripe/customers is skipped).
  authPayload?: { token?: string; accessToken?: string } & Record<string, any>;
  // Skip subscription products and fetch only onetime ones (change-payment-method).
  onetimeOnly?: boolean;
};

/**
 * Bootstraps everything Stripe needs before a card purchase can happen:
 *   1. obtains the Bearer token used by POST /stripe/one-time-payment — either
 *      from the pre-created user's `authPayload`, or by (up)serting a Stripe
 *      customer (POST /stripe/customers) when no payload is supplied;
 *   2. fetches subscription + onetime products and stashes them on `window`
 *      (read by handleSubmitStripe — trial via one-time-payment, then subscribe);
 *   3. initializes the Stripe.js instance (consumed by the <Elements> provider).
 *
 * Shared by the email page (regular flow → creates the customer) and the
 * subscription flow (?flow=subscription&id=... → reuses the existing user's
 * token) so both behave identically for Stripe.
 */
export const setupStripePayment = async ({
  email,
  dispatch,
  paymentSystem,
  authPayload,
  onetimeOnly = false,
}: Params) => {
  let authSource = authPayload;

  if (!authSource) {
    console.log('[setupStripePayment] 1. Creating Stripe customer with email:', email);
    authSource = await Billing.createStripeCustomer(email);
    console.log('[setupStripePayment] 1. Stripe customer response:', authSource);
  } else {
    console.log('[setupStripePayment] 1. Using pre-created user auth (skipping customer creation)');
  }

  if (!authSource) {
    return;
  }

  console.log('[setupStripePayment] 2. Setting up auth and initializing billing');
  await applyFoodzeeAuthTokens(authSource, dispatch, {
    reinitBilling: true,
    paymentSystem,
  });

  // change-payment-method only needs onetime products.
  if (!onetimeOnly) {
    console.log('[setupStripePayment] 3. Fetching Stripe subscription products');
    const subscriptionProductsResponse = await Billing.getStripeProducts('subscription');
    console.log('[setupStripePayment] 3. Stripe subscription products:', subscriptionProductsResponse);

    if (subscriptionProductsResponse?.subscriptions) {
      (window as any).__stripe_subscription_products__ = subscriptionProductsResponse.subscriptions;
      console.log('[setupStripePayment] 3. Stripe subscription products stored in window');
    }
  }

  console.log('[setupStripePayment] 3b. Fetching Stripe onetime products');
  const onetimeProductsResponse = await Billing.getStripeProducts('onetime');
  console.log('[setupStripePayment] 3b. Stripe onetime products:', onetimeProductsResponse);

  if (onetimeProductsResponse?.oneTime) {
    (window as any).__stripe_onetime_products__ = onetimeProductsResponse.oneTime;
    console.log('[setupStripePayment] 3b. Stripe onetime products stored in window');
  }

  console.log('[setupStripePayment] 4. Initializing Stripe');
  const publicKeys = getPublicKeys();
  const publicKey = publicKeys?.stripe?.public_key;
  console.log('[setupStripePayment] 4. Public key available:', !!publicKey);

  if (publicKey && (window as any).Stripe) {
    try {
      const stripeInstance = (window as any).Stripe(publicKey);
      console.log(
        '[setupStripePayment] 4. Stripe initialized successfully with key:',
        publicKey.substring(0, 10) + '...'
      );
      (window as any).__stripe_instance__ = stripeInstance;
    } catch (stripeError) {
      console.error('[setupStripePayment] 4. Error initializing Stripe:', stripeError);
    }
  } else {
    console.warn(
      '[setupStripePayment] 4. Cannot initialize Stripe - missing publicKey or Stripe library',
      {
        publicKey: !!publicKey,
        hasStripeLibrary: !!(window as any).Stripe,
      }
    );
  }

  console.log('[setupStripePayment] All Stripe initialization complete');
};

/**
 * change-payment-method buys a single onetime product chosen by the URL `product`
 * name. handleSubmitStripe charges `__stripe_onetime_products__[0]`, so we move the
 * product whose name matches to the front of that list. Matches on the common name
 * fields (name / productName / productCode / title) and id fields, just in case.
 */
export const pinStripeOnetimeProductByName = (name?: string | null) => {
  if (!name) return;

  const products = (window as any).__stripe_onetime_products__ as any[] | undefined;
  if (!Array.isArray(products) || !products.length) return;

  const matched = products.find((p) =>
    [p?.name, p?.productName, p?.productCode, p?.title, p?.productId, p?.id].includes(name)
  );

  if (!matched) {
    console.warn('[setupStripePayment] No onetime product matched URL name:', name);
    return;
  }

  (window as any).__stripe_onetime_products__ = [
    matched,
    ...products.filter((p) => p !== matched),
  ];
  console.log('[setupStripePayment] Pinned onetime product for purchase:', name);
};
